// Entrar na conta — o MESMO formulário no site e no backoffice:
// email + palavra-passe → (se a conta tiver 2FA) código TOTP → sessão. Botões Discord/Google se configurados.
import { authErrorKey } from "@ecliptica/api-client/errors";
import { useAuthClient, usePublicInfo, useRefreshMe } from "@ecliptica/api-client/react";
import { useTranslation } from "@ecliptica/i18n";
import { Alert, Button, Field, Label } from "@ecliptica/ui";
import { useState, type FormEvent, type ReactNode } from "react";

const PROVIDER_NAME = { discord: "Discord", google: "Google" } as const;

export function SignInForm({ onSignedIn, socialCallbackURL, footer }: { onSignedIn: () => void; socialCallbackURL?: string; footer?: ReactNode }) {
  const { t } = useTranslation("common");
  const auth = useAuthClient();
  const refreshMe = useRefreshMe();
  const info = usePublicInfo();
  const [step, setStep] = useState<"password" | "totp">("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [trust, setTrust] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unverified, setUnverified] = useState(false);
  const [resent, setResent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function done() {
    await refreshMe();
    onSignedIn();
  }

  async function submitPassword(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setUnverified(false);
    const { data, error: err } = await auth.signIn.email({ email, password });
    setBusy(false);
    if (err) {
      if (err.code === "EMAIL_NOT_VERIFIED") setUnverified(true);
      return setError(authErrorKey(err));
    }
    if (data && "twoFactorRedirect" in data && data.twoFactorRedirect) return setStep("totp");
    await done();
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error: err } = await auth.twoFactor.verifyTotp({ code: code.replace(/\s/g, ""), trustDevice: trust });
    setBusy(false);
    if (err) return setError(authErrorKey({ ...err, code: err.code ?? "INVALID_CODE" }));
    await done();
  }

  async function resend() {
    await auth.sendVerificationEmail({ email });
    setResent(true);
  }

  if (step === "totp") {
    return (
      <form onSubmit={submitCode} className="flex flex-col gap-4">
        <div>
          <h2 className="text-xl font-extrabold text-gold">{t("auth.twoFactorTitle")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("auth.twoFactorPrompt")}</p>
        </div>
        <Field
          label={t("auth.code")}
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9 ]{6,7}"
          maxLength={7}
          required
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className="[&_input]:text-center [&_input]:font-mono [&_input]:text-lg [&_input]:tracking-[0.4em]"
        />
        <Label className="flex items-center gap-2 font-normal">
          <input type="checkbox" checked={trust} onChange={(e) => setTrust(e.target.checked)} className="size-4 accent-[var(--ec-gold)]" />
          {t("auth.trustDevice")}
        </Label>
        {error && <Alert variant="destructive">{t(error)}</Alert>}
        <Button type="submit" variant="primary" disabled={busy}>
          {t("auth.verify")}
        </Button>
      </form>
    );
  }

  const providers = info.data?.socialProviders ?? [];
  return (
    <form onSubmit={submitPassword} className="flex flex-col gap-4">
      <Field label={t("auth.email")} type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
      <Field label={t("auth.password")} type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
      {error && (
        <Alert variant="destructive">
          {t(error)}
          {unverified && !resent && (
            <Button type="button" variant="link" size="sm" className="ml-1 h-auto p-0" onClick={() => void resend()}>
              {t("auth.resend")}
            </Button>
          )}
          {resent && <span className="ml-1">{t("auth.resent")}</span>}
        </Alert>
      )}
      <Button type="submit" variant="primary" disabled={busy}>
        {t("auth.signIn")}
      </Button>
      {providers.length > 0 && (
        <>
          <p className="text-center text-xs uppercase tracking-wider text-muted-foreground">{t("auth.or")}</p>
          {providers.map((p) => (
            <Button key={p} type="button" variant="outline" onClick={() => void auth.signIn.social({ provider: p, callbackURL: socialCallbackURL ?? window.location.href })}>
              {t("auth.withProvider", { provider: PROVIDER_NAME[p] })}
            </Button>
          ))}
        </>
      )}
      {footer}
    </form>
  );
}
